import React, {
  useMemo,
  useState,
} from 'react';

import {
  Loader2,
  Trash2,
  UserRound,
} from 'lucide-react';

import {
  removeTournamentParticipant,
} from '../services/tournament.service';

import type {
  TournamentListItem,
  TournamentParticipantsData,
} from '../types/tournament.types';


type TournamentParticipantListProps = {
  tournament: TournamentListItem;

  data:
    TournamentParticipantsData;

  onReload: () => Promise<void>;
};


const TournamentParticipantList:
  React.FC<
    TournamentParticipantListProps
  > = ({
    tournament,
    data,
    onReload,
  }) => {
    const [
      removingKey,
      setRemovingKey,
    ] =
      useState<
        string | null
      >(null);


    /**
     * userId 기준으로
     * 몇 번째 엔트리인지 계산합니다.
     *
     * 동일 회원이 여러 번 등록된 경우
     * 두 번째 엔트리부터
     * RE-ENTRY #2, #3 ... 으로 표시합니다.
     */
    const entryOrderMap =
      useMemo(() => {
        const countMap =
          new Map<
            number,
            number
          >();

        const result =
          new Map<
            number,
            number
          >();

        data.participants.forEach(
          (participant) => {
            const current =
              countMap.get(
                participant.userId,
              ) ?? 0;

            const next =
              current + 1;

            countMap.set(
              participant.userId,
              next,
            );

            result.set(
              participant.participantId,
              next,
            );
          },
        );

        return result;
      }, [
        data.participants,
      ]);


    /**
     * 참가 엔트리 삭제
     *
     * participantId 기준으로
     * 해당 엔트리 하나만 삭제합니다.
     */
    const handleRemoveEntry =
      async (
        participant:
          TournamentParticipantsData[
            'participants'
          ][number],
      ): Promise<void> => {
        const displayName =
          participant.userNickname ||
          participant.userName;

        const confirmed =
          window.confirm(
            [
              `${displayName}님의 참가 엔트리를 삭제하시겠습니까?`,
              '',
              `대회: ${tournament.tournamentTitle}`,
              `Participant ID: ${participant.participantId}`,
              '',
              `시드 ${tournament.tournamentPrice.toLocaleString()}개가 환불됩니다.`,
            ].join('\n'),
          );

        if (!confirmed) {
          return;
        }

        const key =
          `${tournament.tournamentId}-${participant.participantId}`;

        try {
          setRemovingKey(
            key,
          );

          await removeTournamentParticipant(
            tournament.tournamentId,
            participant.participantId,
          );

          await onReload();

          window.alert(
            '참가 엔트리가 삭제되었습니다.',
          );
        } catch (error) {
          console.error(
            '참가 엔트리 삭제 실패:',
            error,
          );

          window.alert(
            '참가 엔트리 삭제에 실패했습니다.',
          );
        } finally {
          setRemovingKey(
            null,
          );
        }
      };


    if (
      data.participants.length ===
      0
    ) {
      return (
        <div className="rounded-xl bg-white px-4 py-8 text-center text-sm text-gray-500">
          참가자가 없습니다.
        </div>
      );
    }


    return (
      <div className="space-y-2">
        {data.participants.map(
          (
            participant,
            index,
          ) => {
            const entryOrder =
              entryOrderMap.get(
                participant.participantId,
              ) ?? 1;

            const isReentry =
              entryOrder > 1;

            const actionKey =
              `${tournament.tournamentId}-${participant.participantId}`;

            const isRemoving =
              removingKey ===
              actionKey;


            return (
              <div
                key={
                  participant.participantId
                }
                className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:flex-row sm:items-center"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gray-100 text-gray-600">
                  <UserRound
                    size={18}
                  />
                </span>


                <span className="w-6 shrink-0 text-center text-xs font-bold text-gray-400">
                  {index + 1}
                </span>


                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <strong className="truncate text-sm text-gray-900">
                      {participant.userNickname ||
                        participant.userName}
                    </strong>


                    {isReentry && (
                      <span className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
                        RE-ENTRY #{entryOrder}
                      </span>
                    )}


                    <span className="shrink-0 text-xs text-gray-400">
                      PID{' '}
                      {
                        participant.participantId
                      }
                    </span>
                  </div>


                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {participant.phoneNumber ??
                      '등록된 번호 없음'}
                  </p>
                </div>


                <button
                  type="button"
                  onClick={() =>
                    void handleRemoveEntry(
                      participant,
                    )
                  }
                  disabled={
                    isRemoving
                  }
                  className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 text-xs font-bold text-red-600 transition hover:bg-red-100 disabled:cursor-wait disabled:opacity-60"
                >
                  {isRemoving ? (
                    <Loader2
                      size={14}
                      className="animate-spin"
                    />
                  ) : (
                    <Trash2
                      size={14}
                    />
                  )}

                  취소
                </button>
              </div>
            );
          },
        )}
      </div>
    );
  };


export default TournamentParticipantList;